package dev.lingvo.site.components.marketing

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.dom.svg.Path
import com.varabyte.kobweb.compose.dom.svg.SVGFillType
import com.varabyte.kobweb.compose.dom.svg.SVGStrokeLineCap
import com.varabyte.kobweb.compose.dom.svg.SVGStrokeLineJoin
import com.varabyte.kobweb.compose.dom.svg.SVGStrokeType
import com.varabyte.kobweb.compose.dom.svg.Svg
import com.varabyte.kobweb.compose.dom.svg.ViewBox
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.letterSpacing
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.FileFormat
import dev.lingvo.site.ds.IconSize
import dev.lingvo.site.ds.Motion
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.displayFont
import dev.lingvo.site.ds.iconSize
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.ds.transitionColors
import dev.lingvo.site.i18n.I18n
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Code
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Pre
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

/**
 * The objection band: eleven formats in, eleven formats out, and the plural key survives the trip.
 *
 * Every localisation tool claims a format list. The claim a reader actually cares about is narrower
 * and harder: that the file they push comes back as *their* file — same keys, same order, same
 * plural categories — rather than as this tool's idea of it re-serialised into their extension.
 * A list of names cannot make that argument, so this band does not make one. It shows a single
 * Russian plural key written out four times, once per format, and lets the reader confirm that the
 * same four categories are present in each.
 *
 * **Russian, and not by accident.** English has two plural forms and would prove nothing: a tool
 * that drops `few` and `many` looks correct in English right up until it ships to Moscow. Russian's
 * `one`/`few`/`many` is the smallest example where a lossy round trip is visible on the page.
 *
 * **The four are the ones with real structure.** Android's `<plurals>`, Apple's `.stringsdict`,
 * nested JSON and YAML each encode plural categories in a different shape — nested elements, a
 * property list's alternating key/value pairs, an object, and significant indentation. A fifth card
 * would cost a column and add no new argument.
 *
 * **The fourth card was Gettext and could not be.** It showed `msgstr[0..2]` carrying Russian's
 * three forms, and this product cannot write that file: `PoFormat.SUPPORTED_PLURAL_CATEGORIES` is
 * `{one, other}`, and `ParsedEntry.fitTo` in `ImportExportRoutes` refuses the export outright — its
 * error names Android XML, `.stringsdict` and the String Catalog as the formats that can carry the
 * key instead. A band whose entire subject is that your file survives the round trip cannot open
 * with a file the round trip rejects. YAML replaces it because `YamlFormat.encodeEntry` writes every
 * category it is handed, so the card is a file this product actually produces.
 *
 * Replaces `FormatsStrip` in `WorkflowSections.kt`, which answered the same objection with a
 * heading and eleven chips. Both own the `formats-title` id; only one of them may be on a page.
 */
@Composable
fun FormatsBand() {
    val palette = ColorMode.current.toSitePalette()
    Band(ground = BandGround.PaperSunken, labelledBy = FORMATS_HEADING_ID) {
        Div(FormatsHeadStyle.toModifier().toAttrs()) {
            Div(FormatsProvenanceStyle.toModifier().toAttrs()) {
                RoundTripIcon()
                Span(FormatsProvenanceTextStyle.toModifier().toAttrs()) {
                    Text(t("marketing.formats_band.supported_imports_exports", "Supported imports and exports"))
                }
            }
            // Sentence case in the DOM; [BandLabelStyle] uppercases it in CSS. A screen reader given
            // literal capitals reads "ELEVEN" as a shout or spells it out, and the caps here are
            // typography rather than meaning.
            BandLabel(
                tf(
                    "marketing.formats_band.supported_format_count",
                    "{count} supported formats",
                    "count" to spelledCount(FileFormat.entries.size),
                ),
            )
            BandHeading(
                text = t("marketing.formats_band.keep_app_resource_formats", "Keep your app’s resource formats."),
                id = FORMATS_HEADING_ID,
            )
        }

        Ul(SpecimenGridStyle.toModifier().toAttrs()) {
            SPECIMENS.forEach { specimen ->
                // Read here rather than inside `toAttrs`, which is not a composable scope: the
                // lookup has to happen in the composition and be captured by the attribute builder.
                val summary = specimenSummary(specimen.format)
                // The list item *is* the card. A wrapper would only exist to be a grid item, and the
                // grid already has one.
                Li(SpecimenCardStyle.toModifier().toAttrs()) {
                    Div(SpecimenHeadStyle.toModifier().toAttrs()) {
                        Div(SpecimenNameStyle.toModifier().toAttrs()) { Text(specimen.name) }
                        Div(SpecimenPathStyle.toModifier().toAttrs()) { Text(specimen.path) }
                    }
                    Pre(
                        SpecimenCodeStyle.toModifier().toAttrs {
                            // One label instead of five lines of angle brackets read out character by
                            // character. The specimen is evidence for a sighted reader comparing four
                            // shapes at a glance; spoken, that comparison is the summary and the
                            // punctuation is noise. Same reasoning as `CliTranscript`.
                            attr("role", "img")
                            attr("aria-label", summary)
                        },
                    ) {
                        Code(Modifier.toAttrs()) {
                            specimen.lines.forEachIndexed { index, line ->
                                Span(
                                    Modifier.color(
                                        when (line.ink) {
                                            CodeInk.Scaffold -> palette.textMuted
                                            CodeInk.Form -> palette.textSecondary
                                            CodeInk.Focus -> palette.textPrimary
                                        },
                                    ).toAttrs(),
                                ) {
                                    Text(line.before)
                                    if (line.ru.isNotEmpty()) {
                                        // Load-bearing: the Cyrillic is what the reader is asked to
                                        // compare across four cards, and `lang` is what tells a screen
                                        // reader to switch voice and the `:lang()` rules in `Tokens.kt`
                                        // which face to set it in.
                                        Span(Modifier.toAttrs { attr("lang", "ru") }) { Text(line.ru) }
                                    }
                                    Text(line.after)
                                }
                                // Between lines only. A trailing newline inside `<pre>` is a blank
                                // sixth row, and the four cards would stop bottoming out together.
                                if (index != specimen.lines.lastIndex) Text("\n") // i18n-ignore: a line break, not a sentence
                            }
                        }
                    }
                }
            }
        }

        Div(FormatsRestStyle.toModifier().toAttrs()) {
            P(FormatsRestLabelStyle.toModifier().toAttrs()) {
                Text(
                    tf("marketing.formats_band.and_count_more", "and {count} more", "count" to spelledCount(REST.size)),
                )
            }
            Ul(FormatsChipListStyle.toModifier().toAttrs()) {
                REST.forEach { format ->
                    Li(FormatsChipStyle.toModifier().toAttrs()) { Text(format.displayName) }
                }
            }
        }
    }
}

/** Shared with `FormatsStrip`, which this replaces. Two elements may not carry it at once. */
private const val FORMATS_HEADING_ID = "formats-title"

/**
 * Three steps of the ink ramp, standing in for syntax colour.
 *
 * A marketing page that introduces a fifth and sixth hue to colour XML attributes has stopped being
 * this page. Structure reads perfectly well from weight of ink alone: the container syntax recedes,
 * the plural machinery sits mid-ramp, and the line the eye should land on takes full ink.
 *
 * Which line takes [Focus] is a reading decision per specimen. Three of the four highlight the `one`
 * form, so the identical Russian word can be found in the same relative place in each. The
 * `.stringsdict` is the exception and highlights both `<string>` values, because its keys and values
 * alternate — highlighting one value there would read as an arbitrary stripe rather than as the
 * translated text.
 */
private enum class CodeInk { Scaffold, Form, Focus }

/**
 * One line of a specimen, split at the Cyrillic.
 *
 * The split exists so the Russian can carry `lang="ru"` without the surrounding code carrying it
 * too. Tagging the whole line would tell the browser that `<item quantity="one">` is Russian, which
 * is both false and — once a script face is bound to `ru` — visible.
 */
private data class CodeLine(
    val ink: CodeInk,
    val before: String,
    val ru: String = "",
    val after: String = "",
)

private data class Specimen(
    /** The enum member, so [REST] can be computed rather than typed out and drift. */
    val format: FileFormat,
    val name: String,
    val path: String,
    val lines: List<CodeLine>,
)

/**
 * The same key, four times.
 *
 * `items` with Russian's three cardinal categories in each — `one` (товар), `few` (товара) and
 * `many` (товаров). The word is the same word in all four cards on purpose: the argument is that
 * nothing was reshuffled, and a reader can only check that if there is something to check.
 *
 * The card titles are the design's own labels rather than [FileFormat.displayName], which is longer
 * and, for the Apple entry, names the file type rather than the platform the reader is scanning for.
 * The `format` field keeps the mapping honest, and the chip row is derived from it.
 */
private val SPECIMENS = listOf(
    Specimen(
        format = FileFormat.ANDROID_XML,
        name = "Android XML",
        path = "values-ru/strings.xml",
        lines = listOf(
            CodeLine(CodeInk.Scaffold, "<plurals name=\"items\">"),
            CodeLine(CodeInk.Form, "  <item quantity=\"one\">"),
            CodeLine(CodeInk.Focus, "    %d ", "товар"),
            CodeLine(CodeInk.Form, "  </item>"),
            CodeLine(CodeInk.Scaffold, "</plurals>"),
        ),
    ),
    Specimen(
        format = FileFormat.IOS_STRINGSDICT,
        name = "Apple",
        path = "ru.lproj/Localizable.stringsdict",
        lines = listOf(
            // The pen abbreviates this to `NSStringPluralRule…` because its card was 190px wide.
            // This column is wider, and a truncated key in a specimen undercuts the claim the
            // specimen is making. The real key fits, so it is the real key.
            CodeLine(CodeInk.Scaffold, "<key>NSStringPluralRuleType</key>"),
            CodeLine(CodeInk.Form, "<key>one</key>"),
            CodeLine(CodeInk.Focus, "<string>%d ", "товар", "</string>"),
            CodeLine(CodeInk.Form, "<key>few</key>"),
            CodeLine(CodeInk.Focus, "<string>%d ", "товара", "</string>"),
        ),
    ),
    Specimen(
        format = FileFormat.JSON_NESTED,
        name = "JSON · nested",
        path = "locales/ru.json",
        lines = listOf(
            CodeLine(CodeInk.Scaffold, "\"items\": {"),
            CodeLine(CodeInk.Focus, "  \"one\": \"{count} ", "товар", "\","),
            CodeLine(CodeInk.Form, "  \"few\": \"{count} ", "товара", "\","),
            CodeLine(CodeInk.Form, "  \"many\": \"{count} ", "товаров", "\""),
            CodeLine(CodeInk.Scaffold, "}"),
        ),
    ),
    Specimen(
        format = FileFormat.YAML,
        name = "YAML",
        // The layout `FormatRoutes.detectionExamples` advertises for this format, with the locale
        // root a Rails catalogue actually carries. `YamlFormat.flatten` reads it as `ru.items`.
        path = "config/locales/ru.yml",
        lines = listOf(
            CodeLine(CodeInk.Scaffold, "ru:"),
            CodeLine(CodeInk.Scaffold, "  items:"),
            // `%{count}`, which is the placeholder syntax `YamlFormat` declares. A specimen that
            // used another library's braces would be advertising a file this parser would read
            // differently from the reader's own i18n gem.
            CodeLine(CodeInk.Focus, "    one: \"%{count} ", "товар", "\""),
            CodeLine(CodeInk.Form, "    few: \"%{count} ", "товара", "\""),
            CodeLine(CodeInk.Form, "    many: \"%{count} ", "товаров", "\""),
        ),
    ),
)

/**
 * What a screen reader is given in place of the code, in the reader's own language.
 *
 * Looked up here rather than held in [SPECIMENS], and that is the same decision the price card and
 * the limits rows already made: `SPECIMENS` is a top-level `val` evaluated at class-init, before any
 * catalogue is fetched, so a lookup written into it would return its own English for the life of the
 * page. The key is a literal per format because a key built by interpolation is a key
 * `extract-strings.mjs` cannot see, and a string it cannot see is one no translator is ever offered.
 *
 * These four sentences are the *only* thing assistive technology gets from this band — the code
 * itself is `role="img"` — so leaving them in English left the band's whole argument monolingual on
 * a localization product's own page.
 */
@Composable
private fun specimenSummary(format: FileFormat): String = when (format) {
    FileFormat.ANDROID_XML -> t(
        "marketing.formats_band.android_xml_a_plurals_block_named",
        "Android XML: a plurals block named items, holding one item element per Russian " +
            "plural category.",
    )
    FileFormat.IOS_STRINGSDICT -> t(
        "marketing.formats_band.apple_stringsdict_an_nsstringpluralruletype_entr",
        "Apple stringsdict: an NSStringPluralRuleType entry with a key and string pair " +
            "for the one and few categories.",
    )
    FileFormat.JSON_NESTED -> t(
        "marketing.formats_band.nested_json_an_items_object_with",
        "Nested JSON: an items object with one, few and many keys.",
    )
    FileFormat.YAML -> t(
        "marketing.formats_band.yaml_an_items_mapping_under_the",
        "YAML: an items mapping under the ru locale, with one, few and many keys.",
    )
    // A fifth card would arrive here with the format's own name as its accessible label, which is
    // thin but is not a lie. Silently reusing another card's sentence would be.
    else -> format.displayName
}

/**
 * The seven formats without a card, taken from [FileFormat] rather than written out.
 *
 * They are `iOS .strings`, `Apple String Catalog`, `JSON (flat)`, `Java properties`, `CSV`,
 * `XLIFF 1.2` and `Gettext PO` — not a selection, but *everything the four cards do not already
 * show*, which is the only honest way to say "and seven more" under a heading that says eleven.
 * Subtracting the cards from the enum is also what keeps the three counts on this band — eleven,
 * four, seven — from ever disagreeing after someone adds a twelfth parser.
 *
 * The row says these formats exist, and deliberately claims nothing about the plural key above it:
 * `PROPERTIES` and `XLIFF` throw on a plural export, `IOS_STRINGS` and `CSV` drop a plural-only
 * entry, and `PO` carries `{one, other}` only. A chip is a name, not a promise.
 *
 * Each chip carries [FileFormat.displayName] verbatim, so the page cannot advertise a format under
 * a name the importer would not answer to.
 */
private val REST: List<FileFormat> =
    FileFormat.entries.filterNot { format -> SPECIMENS.any { it.format == format } }

/**
 * "Eleven", not "11".
 *
 * These are sentences, and a numeral in the middle of one reads as a spec sheet. Spelling out a
 * count from [FileFormat.entries] rather than hardcoding the word is what stops the heading claiming
 * eleven formats on the day the twelfth ships — the failure this band would be most embarrassed by,
 * since its whole subject is a promise about a list.
 */
/**
 * Small numbers spelled out — in English, and only in English.
 *
 * "and eight more" reads better than "and 8 more", but spelling out small numbers is an English
 * typographic convention rather than a universal one, and this file has no idea how any other
 * language writes its numbers. Outside the default locale the numeral is the honest answer; shipping
 * [NUMBER_WORDS] to every reader would be `if (count == 1)` deciding plurals for the world, one
 * band over from the section that exists to argue against exactly that.
 */
private fun spelledCount(count: Int): String =
    if (I18n.locale == I18n.DEFAULT_LOCALE) spellOut(count) else count.toString()

private fun spellOut(count: Int): String = NUMBER_WORDS.getOrElse(count) { count.toString() }

private val NUMBER_WORDS = listOf(
    "zero", "one", "two", "three", "four", "five", "six",
    "seven", "eight", "nine", "ten", "eleven", "twelve",
)

/**
 * The round-trip mark: out and back, drawn once.
 *
 * Two arrows describing a loop rather than a one-way arrow, because "in and out" is the claim and a
 * single arrow would illustrate an import. Local to this file rather than added to `Icons.kt` — it
 * is a piece of this band's argument, not a control glyph the product reaches for.
 */
@Composable
private fun RoundTripIcon() {
    Svg(
        attrs = RoundTripIconStyle.toModifier().toAttrs {
            viewBox(ViewBox.sized(24))
            fill(SVGFillType.None)
            stroke(SVGStrokeType.CurrentColor)
            strokeWidth(2)
            strokeLineCap(SVGStrokeLineCap.Round)
            strokeLineJoin(SVGStrokeLineJoin.Round)
            attr("aria-hidden", "true")
            attr("focusable", "false")
        },
    ) {
        ROUND_TRIP_PATHS.forEach { path -> Path { d(path) } }
    }
}

private val ROUND_TRIP_PATHS = listOf(
    "m17 2 4 4-4 4",
    "M3 11v-1a4 4 0 0 1 4-4h14",
    "m7 22-4-4 4-4",
    "M21 13v1a4 4 0 0 1-4 4H3",
)

val FormatsHeadStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .gap(Space.LG)
        .styleModifier {
            property("display", "flex")
            property("flex-direction", "column")
            property("align-items", "flex-start")
        }
}

val FormatsProvenanceStyle = CssStyle.base {
    Modifier
        .gap(Space.SM)
        .styleModifier {
            property("display", "flex")
            property("align-items", "center")
        }
}

/**
 * A glyph box, not a spacing step.
 *
 * The icon is optically matched to the cap height of the mono line beside it, so it takes the icon
 * ladder's [IconSize.MD] rung — the token that exists for exactly this. `Space.LG` happens to be the
 * same number and would be a coincidence dressed as a token. It was previously two raw `16.px`
 * modifiers, which is the shape [dev.lingvo.site.ds.iconSize] exists to prevent: a width and a
 * height that can be set to disagree.
 */
val RoundTripIconStyle = CssStyle.base {
    Modifier
        .iconSize(IconSize.MD)
        .color(colorMode.toSitePalette().accent)
        .styleModifier {
            property("display", "block")
            property("flex", "0 0 auto")
        }
}

val FormatsProvenanceTextStyle = CssStyle.base {
    Modifier
        .monoFont()
        .fontSize(TypeScale.Micro)
        .color(colorMode.toSitePalette().textMuted)
}

/**
 * Four columns, then two, then one.
 *
 * A grid rather than a flex row because the four cards must share a width exactly — the reader is
 * comparing them, and a column that is 12px wider because Gettext has shorter lines would read as a
 * difference between the formats. `minmax(0, 1fr)` rather than `1fr` so a code line longer than its
 * column scrolls inside the card instead of stretching the track and pushing the band sideways.
 */
val SpecimenGridStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .gap(Space.LG)
            .margin(0.px)
            .padding(0.px)
            .styleModifier {
                property("display", "grid")
                property("grid-template-columns", "repeat(4, minmax(0, 1fr))")
                property("list-style", "none")
            }
    }
    // Four specimens at 10.5px mono in a phone-width column would be four unreadable slivers.
    Breakpoint.ZERO { Modifier.styleModifier { property("grid-template-columns", "minmax(0, 1fr)") } }
    Breakpoint.MD { Modifier.styleModifier { property("grid-template-columns", "repeat(2, minmax(0, 1fr))") } }
    Breakpoint.LG { Modifier.styleModifier { property("grid-template-columns", "repeat(4, minmax(0, 1fr))") } }
}

/**
 * Paper on the band's sunken ground, with the one hover in this band.
 *
 * **Hover tints one step and does nothing else.** The card does not lift, scale or shadow: four
 * specimens sitting in a row are being compared, and a card that rises out of the row breaks the
 * baseline the comparison depends on. One step up the fill ladder — `surfaceBase` to `surface` — is
 * enough to mark which one you are reading, and at [Motion.FastMs] it tracks the pointer instead of
 * trailing it.
 *
 * `overflow: hidden` so the header's sunken fill is cut by the card's radius; without it the fill
 * squares off the top two corners.
 */
val SpecimenCardStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .backgroundColor(colorMode.toSitePalette().surfaceBase)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
            .borderRadius(Radius.Card)
            .styleModifier {
                property("display", "flex")
                property("flex-direction", "column")
                property("overflow", "hidden")
            }
            .transitionColors(durationMs = Motion.FastMs)
    }
    hover {
        Modifier.backgroundColor(colorMode.toSitePalette().surface)
    }
}

/**
 * The header sits on the sunken fill so the code body reads as the page beneath a label.
 *
 * The divider is on the bottom edge, which is the only edge a divider may be on here: a rule down
 * the leading edge of a card is banned system-wide, and this one is separating a header from a body
 * rather than marking the card as a category.
 */
val SpecimenHeadStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .backgroundColor(colorMode.toSitePalette().surfaceSunken)
        .padding(Space.LG)
        .gap(Space.XS)
        .styleModifier {
            property("display", "flex")
            property("flex-direction", "column")
            property("border-bottom", "1px solid ${colorMode.toSitePalette().border}")
        }
}

val SpecimenNameStyle = CssStyle.base {
    Modifier
        .displayFont()
        .fontSize(TypeScale.BodySmall)
        .fontWeight(700)
        // Tracking, not spacing: the display face sets loose at 15px next to a mono line, and this
        // pulls the format name back to the density of the path underneath it.
        .letterSpacing((-0.3).px)
        .color(colorMode.toSitePalette().textPrimary)
}

val SpecimenPathStyle = CssStyle.base {
    Modifier
        .monoFont()
        .fontSize(TypeScale.Micro)
        .color(colorMode.toSitePalette().textMuted)
        // `ru.lproj/Localizable.stringsdict` has no spaces to break at, so a narrow column would
        // otherwise widen its grid track to fit the whole path.
        .styleModifier { property("overflow-wrap", "anywhere") }
}

/**
 * The code body.
 *
 * `<pre>` keeps the leading spaces that carry the nesting — the indentation is the structure here,
 * and a `<div>` per line would collapse it. `overflow-x: auto` is a net rather than a feature: the
 * longest line is 33 characters and fits every breakpoint's column, but a specimen that silently
 * widens the page would be worse than one that scrolls.
 */
val SpecimenCodeStyle = CssStyle.base {
    Modifier
        .monoFont()
        .fontSize(TypeScale.Micro)
        .lineHeight(TypeScale.BodyLineHeight)
        .padding(Space.LG)
        .margin(0.px)
        .styleModifier { property("overflow-x", "auto") }
}

val FormatsRestStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .gap(Space.MD)
        .styleModifier {
            property("display", "flex")
            property("flex-wrap", "wrap")
            property("align-items", "center")
        }
}

val FormatsRestLabelStyle = CssStyle.base {
    Modifier
        .monoFont()
        .fontSize(TypeScale.Micro)
        .color(colorMode.toSitePalette().textMuted)
        .margin(0.px)
}

val FormatsChipListStyle = CssStyle.base {
    Modifier
        .gap(Space.MD)
        .margin(0.px)
        .padding(0.px)
        .styleModifier {
            property("display", "flex")
            property("flex-wrap", "wrap")
            property("align-items", "center")
            property("list-style", "none")
        }
}

/**
 * A quiet chip, and deliberately not `LChip`.
 *
 * `LChip` is shaped like a control — raised fill, `borderStrong` boundary, a hover tint — and these
 * seven names do nothing when you point at them. On this band the mismatch is also visual: a chip
 * on `surfaceRaised` would sit brighter than the paper specimens it is subordinate to, and the row
 * that exists to say "there are more, they are unremarkable" would be the loudest thing under the
 * heading. Paper on the sunken ground with a hairline puts it where it belongs.
 */
val FormatsChipStyle = CssStyle.base {
    Modifier
        .backgroundColor(colorMode.toSitePalette().surfaceBase)
        .color(colorMode.toSitePalette().textSecondary)
        .border(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
        .borderRadius(Radius.Pill)
        .padding(topBottom = Space.SM, leftRight = Space.MD)
        .monoFont()
        .fontSize(TypeScale.Micro)
        .lineHeight(1.0)
        .styleModifier {
            property("display", "inline-flex")
            property("align-items", "center")
            property("white-space", "nowrap")
        }
}
